// CalendarMonth > CalendarMonth
import React from 'react'
import { CalendarMonthProps, CalendarMonthState } from './ICalendarMonth'
import FlexBox from '../FlexBox'
import Text from '../Text'
import {
	format,
	startOfMonth,
	endOfMonth,
	nextMonth,
	previousMonth,
	daysInMonth,
	startOfWeek,
	dateRangeForMonth,
	dateRange,
	endOfWeek,
	isSameDate
} from '../utils/Dates'
import Grid from '../Grid'
import CalendarMonthDay from './Day'
import styles from './CalendarMonth.module.css'
import { isEqual } from '../utils/Object'

export default class CalendarMonth extends React.PureComponent<CalendarMonthProps, CalendarMonthState> {
	private readonly _daysOfWeek: string[] = []
	private readonly _locale: string
	private _displayDate: Date

	constructor(props: CalendarMonthProps) {
		super(props)
		this._locale = props.locale || 'en-us'
		this._displayDate = startOfMonth(this.props.selected || new Date())

		this.state = this._stateForDate(this._displayDate)

		this.state.dates
			.slice(0, 7)
			.forEach((d) => this._daysOfWeek.push(format(d, this._locale, { weekday: 'short' })))
	}

	componentDidUpdate(prevProps: CalendarMonthProps) {
		if (this.props.selected && !isEqual(this.props.selected, prevProps.selected)) {
			this._displayDate = this.props.selected
			this.setState(this._stateForDate(this.props.selected))
		}
	}

	private _selectNextMonth = () => {
		nextMonth(this._displayDate, true)

		this.setState(this._stateForDate(this._displayDate))
	}

	private _selectPreviousMonth = () => {
		previousMonth(this._displayDate, true)

		this.setState(this._stateForDate(this._displayDate))
	}

	private _stateForDate = (date: Date) => {
		return {
			month: date.getMonth(),
			daysInMonth: daysInMonth(date),
			start: startOfMonth(date),
			end: endOfMonth(date),
			dates: this._getDisplayDatesForDate(date)
		}
	}

	private _getDisplayDatesForDate = (date: Date): Date[] => {
		const dates: Date[] = dateRangeForMonth(date)
		const firstDate = dates[0]
		const lastDate = dates[dates.length - 1]

		// need to prepend some of previous months dates
		if (firstDate.getDay() > 0) {
			const weekStart = startOfWeek(firstDate)
			const toAdd = dateRange(weekStart, firstDate)

			dates.unshift(...toAdd)
		}

		// need to push some of the next months dates
		if (lastDate.getDay() < 6) {
			const weekEnd = endOfWeek(lastDate)
			const toAdd = dateRange(lastDate, weekEnd)
			toAdd.shift() // remove the repeat at the front

			dates.push(...toAdd)
		}

		return dates
	}

	render() {
		const { dates } = this.state
		const { selected } = this.props
		const rows = Math.ceil(dates.length / 7) + 1

		return (
			<FlexBox container direction='column'>
				<FlexBox container>
					<Text className={styles['month-name']}>
						{format(this._displayDate, this._locale, { month: 'long', year: 'numeric' })}
					</Text>
					<button type='button' onClick={this._selectPreviousMonth} className={styles['month-nav']}>
						{'<'}
					</button>
					<button type='button' onClick={this._selectNextMonth} className={styles['month-nav']}>
						{'>'}
					</button>
				</FlexBox>
				<Grid container columns='repeat(7, 1fr)' rows={`repeat(${rows}, 1fr)`}>
					{this._daysOfWeek.map((str) => (
						<Text key={str} className={styles['column-name']}>
							{str}
						</Text>
					))}
					{dates.map((d) => (
						<CalendarMonthDay
							key={d.getTime()}
							isSelected={selected ? isSameDate(d, selected) : false}
							date={d}
							displayDate={this._displayDate}
							onSelect={this.props.onDateSelect}
						/>
					))}
				</Grid>
			</FlexBox>
		)
	}
}
